import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { invokeFunction, supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import OrganisationDetailsForm, { OrganisationDetailsSubmitMeta } from './OrganisationDetailsForm';
import CsvImportStep from './CsvImportStep';
import { CompanyData, Product, Connection } from '@/types';
import { StandardizedConnection } from '@/types/connections';
import { Json } from '@/integrations/supabase/types';
import { logMerchantCreated } from '@/utils/activityLogger';
import { TEST_NAME_PREFIX, hasTestNamePrefix } from '@/shared/merchantStatus';
import { saveOrganisationConnections } from '@/utils/organization/saveOrganization';
import { OnboardingState, withOnboardingColumns } from '@/shared/onboardingState';
import type { TablesInsert } from '@/integrations/supabase/types';
import { isInvalidRevenue, parseRevenue } from '@/utils/organization/revenue';

// A test client always carries the [TEST] prefix (REQ-037).
const withTestPrefix = (name: string, isTestClient: boolean) =>
  isTestClient && !hasTestNamePrefix(name) ? `${TEST_NAME_PREFIX} ${name}` : name;

interface MultiStepOrganisationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => Promise<void>;
  editMode?: boolean;
  organizationId?: string;
  isDraft?: boolean;
  isAdminEdit?: boolean;
}

const MultiStepOrganisationModal: React.FC<MultiStepOrganisationModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  editMode = false,
  organizationId,
  isDraft = false,
  isAdminEdit = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loading, setLoading] = useState(editMode && !!organizationId);
  const [organizationData, setOrganizationData] = useState<CompanyData | null>(null);
  const [csvConnectors, setCsvConnectors] = useState<StandardizedConnection[]>([]);
  // Track the org ID for new organizations (created as draft for CSV import)
  const [draftOrgId, setDraftOrgId] = useState<string | undefined>(organizationId);
  // Admin › Add Merchant (FIG-319) is the create path; edit mode is unchanged.
  const isAdminCreate = !editMode;
  // Editing an existing merchant: its Point of Contact is managed in Merchant Contacts.
  const lockPointOfContact = editMode && !!organizationId;
  const [isTestClient, setIsTestClient] = useState(false);

  // However the Add Merchant flow is left (close, Escape, step 1 Cancel), an
  // abandoned auto-fill must not carry over to the next merchant.
  useEffect(() => {
    if (open || !isAdminCreate) return;
    setIsTestClient(false);
    setOrganizationData(null);
    setCurrentStep(1);
  }, [open, isAdminCreate]);

  // Generate a unique session key for this modal instance
  const getSessionKey = () => `csvConnectors_${organizationId || draftOrgId || 'new'}`;

  // Fetch organization data if in edit mode and restore CSV connectors from session
  useEffect(() => {
    const fetchOrganization = async () => {
      if (editMode && organizationId && open) {
        setLoading(true);
        try {
          let data;
          let error;

          // Fetch submitted organization
          const result = await supabase
            .from('organisations')
            .select('*')
            .eq('id', organizationId)
            .single();

          error = result.error;
          data = result.data;

          if (data) {
            // Transform database organization format to form data format
            const formData: CompanyData = {
              pocName: data.poc_name || '',
              pocEmail: data.poc_email || '',
              pocPhone: data.poc_phone || '',
              name: data.name || '',
              name_dbas: data.name_dbas || '',
              address: data.address || '',
              stateOfIncorporation: data.state_of_incorporation || '',
              ein: data.ein || '',
              revenueLast12Months: data.revenue_last_12_months ?? undefined,
              products: Array.isArray(data.products) ? data.products as Product[] : [],
              terms: true, // Since it's an existing organization, terms were already accepted
              organization_id: organizationId // Include the organization ID
            };

            // Handle connections - normalize from object to array format if needed
            if (data.connections) {
              if (Array.isArray(data.connections)) {
                formData.connections = data.connections as Connection[];
              } else {
                // Convert object format to array
                const connectionsArray: Connection[] = [];
                for (const key in data.connections) {
                  if (data.connections[key]) {
                    connectionsArray.push({
                      ...data.connections[key],
                      id: key
                    });
                  }
                }
                formData.connections = connectionsArray;
              }

              // Check for CSV connections and set csvConnectors
              if (formData.connections && formData.connections.length > 0) {
                const csvConnection = formData.connections.find(conn => conn.type === "CSV");

                if (csvConnection && csvConnection.csvImports && csvConnection.csvImports.length > 0) {
                  const connectors = csvConnection.csvImports.map(csvImport => ({
                    id: csvImport.id || csvConnection.id,
                    name: "CSV Import",
                    description: `CSV file: ${csvImport.fileName}`,
                    logoUrl: 'https://via.placeholder.com/40?text=CSV',
                    status: (csvConnection.status === 'connected' || csvConnection.status === 'pending' || csvConnection.status === 'error')
                      ? csvConnection.status as 'connected' | 'pending' | 'error'
                      : 'connected',
                    connectorId: csvConnection.id,
                    connectionId: csvConnection.connectionId || csvConnection.id,
                    service: 'csv',
                    type: 'csv',
                    created_at: csvConnection.created_at || new Date().toISOString(),
                    file_name: csvImport.fileName,
                    import_type: csvConnection.import_type,
                    ecommerce_subtype: csvConnection.ecommerce_subtype,
                    ...(csvImport.snapshot_connection_id && {
                      metadata: { snapshot_connection_id: csvImport.snapshot_connection_id }
                    })
                  }));
                  setCsvConnectors(connectors);
                }
              }
            }

            setOrganizationData(formData);
          }

          if (error) throw error;

        } catch (error) {
          console.error('Error fetching organization:', error);
          toast.error('Failed to load organization data');
        } finally {
          setLoading(false);
        }
      } else if (open) {
        // For new organizations or when modal opens, try to restore CSV connectors from session
        const sessionKey = getSessionKey();
        const savedConnectors = sessionStorage.getItem(sessionKey);
        if (savedConnectors) {
          try {
            const parsedConnectors = JSON.parse(savedConnectors);
            setCsvConnectors(parsedConnectors);
          } catch (error) {
            console.error('Error parsing saved CSV connectors:', error);
            sessionStorage.removeItem(sessionKey);
          }
        }
      }
    };

    fetchOrganization();
  }, [editMode, organizationId, open, isDraft]);

  const handleStepOneComplete = async (data: CompanyData, meta?: OrganisationDetailsSubmitMeta) => {
    let orgId = organizationId || draftOrgId || data.organization_id;
    const testClient = isAdminCreate && !!meta?.isTestClient;
    setIsTestClient(testClient);

    // For NEW organizations, create a draft record so CSV import has an org ID
    if (!orgId && !editMode) {
      try {
        // FIG-345: onboarding progress lives in onboarding_state; Status is the
        // database default (Onboarding), or Test for an auto-filled test client.
        const { data: newOrg, error } = await supabase
          .from('organisations')
          .insert(withOnboardingColumns<TablesInsert<'organisations'>>({
            name: withTestPrefix(data.name || 'New Organisation', testClient),
            poc_name: data.pocName || '',
            poc_email: data.pocEmail || '',
            poc_phone: data.pocPhone || '',
            ein: data.ein || '',
            address: data.address || '',
            state_of_incorporation: data.stateOfIncorporation || '',
            revenue_last_12_months: parseRevenue(data.revenueLast12Months),
            products: [] as unknown as Json,
          }, { onboarding_state: 'in_progress', ...(testClient ? { status: 'test' as const } : {}) }))
          .select('id')
          .single();

        if (error) throw error;

        orgId = newOrg.id;
        setDraftOrgId(orgId);
      } catch (error) {
        console.error('Failed to create draft organization:', error);
        toast.error('Failed to prepare organization for CSV upload');
        return;
      }
    }

    const updatedData = {
      ...data,
      organization_id: orgId
    };
    setOrganizationData(updatedData);
    setCurrentStep(2);
  };

  const handleBack = () => {
    // Navigate back to step 1 without resetting any data
    setCurrentStep(1);
  };

  const handleSubmit = async () => {
    // Adding a merchant does not require a data upload (REQ-034).
    if (!organizationData || (csvConnectors.length === 0 && !isAdminCreate)) return;
    await finalizeSubmission(csvConnectors, false);
  };

  const handleSaveAsDraft = async () => {
    if (!organizationData) return;
    // Pass CSV connectors array
    await finalizeSubmission(csvConnectors, true);
  };

  const finalizeSubmission = async (connectors: StandardizedConnection[], saveAsDraft: boolean) => {
    if (!organizationData) return;
    // Step 1 rejects this too; never let a mistyped amount clear saved revenue.
    if (isInvalidRevenue(organizationData.revenueLast12Months)) {
      toast.error('Revenue is not a valid amount. Go back to step 1 to correct it.');
      return;
    }

    if (saveAsDraft) {
      setSavingDraft(true);
    } else {
      setSubmitting(true);
    }

    try {
      // Format products data
      const formattedProducts = organizationData.products.map(p => ({
        name: p.name,
        description: p.description,
        category: p.category
      }));

      // Create CSV connector data if connectors exist
      let connections = [...(organizationData.connections || [])];

      if (connectors.length > 0) {
        const csvOrgDebug = '[csv-org-debug]';
        console.info(csvOrgDebug, 'finalizeSubmission: grouping csvConnectors for org save', {
          editMode,
          isAdminEdit,
          effectiveOrgId: organizationId || draftOrgId,
          connectorCount: connectors.length,
          connectorsSummary: connectors.map((c, i) => ({
            index: i,
            id: c.id,
            connectionId: c.connectionId,
            connectorId: c.connectorId,
            file_name: c.file_name,
            import_type: c.import_type,
            ecommerce_subtype: c.ecommerce_subtype,
            idImpliesShopify: String(c.id || '').toLowerCase().includes('_inventory_shopify_'),
            idImpliesAmazon: String(c.id || '').toLowerCase().includes('_inventory_amazon_'),
          })),
        });

        // Read existing DB connections to recover snapshot_connection_id values saved
        // by CsvImportStep.handleCsvSuccess (state metadata can be unreliable for the first connector)
        const effectiveOrgId = organizationId || draftOrgId;
        const dbConnectorById: Record<string, any> = {};
        if (effectiveOrgId) {
          const { data: currentOrg } = await supabase
            .from('organisations')
            .select('connections')
            .eq('id', effectiveOrgId)
            .single();
          if (Array.isArray(currentOrg?.connections)) {
            for (const conn of currentOrg.connections as any[]) {
              if (conn?.id) dbConnectorById[conn.id] = conn;
            }
          }
        }

        // Group connectors by connectionId to create a single CSV connection
        const csvConnectionId = connectors[0].connectionId || connectors[0].id;
        const csvConnectorData: Connection = {
          id: csvConnectionId,
          connectionId: csvConnectionId, // Add this for reports-data compatibility
          name: "CSV Import",
          type: "CSV",
          status: "active",
          created_at: new Date().toISOString(),
          import_type: connectors[0].import_type,
          ecommerce_subtype: connectors[0].ecommerce_subtype,
          csvImports: connectors.map(connector => {
            const snapshotConnId =
              connector.metadata?.snapshot_connection_id ||
              dbConnectorById[connector.id]?.metadata?.snapshot_connection_id ||
              sessionStorage.getItem(`snapshot_conn_id_${connector.id}`);
            const snapDate =
              connector.metadata?.snapshot_date ||
              connector.metadata?.inventory_date ||
              dbConnectorById[connector.id]?.metadata?.snapshot_date ||
              dbConnectorById[connector.id]?.metadata?.inventory_date;
            const sub = String(connector.ecommerce_subtype || '').toLowerCase();
            const invSource =
              sub === 'shopify' ? 'shopify_csv_import' : sub === 'amazon' ? 'amazon_csv_import' : undefined;
            return {
              id: connector.id,
              date: new Date().toISOString(),
              fileName: connector.file_name,
              ...(snapshotConnId && { snapshot_connection_id: snapshotConnId }),
              ...(typeof snapDate === 'string' && snapDate && { snapshot_date: snapDate }),
              ...(invSource && { inventory_source: invSource }),
              ...(connector.metadata?.csv_import_job_id && {
                csv_import_job_id: connector.metadata.csv_import_job_id,
              }),
            };
          })
        };

        // Filter out any existing CSV connection with the same ID
        connections = connections.filter(c => c.id !== csvConnectorData.id);
        connections.push(csvConnectorData);

        console.info(csvOrgDebug, 'finalizeSubmission: built single grouped CSV parent', {
          parentId: csvConnectorData.id,
          parentConnectionId: csvConnectorData.connectionId,
          parentEcommerceSubtypeFromFirstOnly: csvConnectorData.ecommerce_subtype,
          csvImportCount: csvConnectorData.csvImports?.length,
          csvImportIds: (csvConnectorData.csvImports || []).map((imp: { id?: string }) => imp.id),
          note: 'Parent ecommerce_subtype is taken from connectors[0] only; mixed-channel inventory uploads can disagree with later csvImports[].id markers.',
        });
      }

      // Prepare organization data
      const organisation = {
        name: withTestPrefix(organizationData.name, isTestClient),
        name_dbas: organizationData.name_dbas || '',
        ein: organizationData.ein,
        address: organizationData.address || '',
        state_of_incorporation: organizationData.stateOfIncorporation || '',
        revenue_last_12_months: parseRevenue(organizationData.revenueLast12Months),
        poc_name: organizationData.pocName,
        poc_email: organizationData.pocEmail,
        poc_phone: organizationData.pocPhone,
        products: formattedProducts as unknown as Json,
        connections: connections as unknown as Json,
      };
      // FIG-345 (UC-225): finishing or saving a draft moves onboarding progress,
      // never the relationship Status.
      const onboardingStateFor = (draft: boolean): OnboardingState => (draft ? 'in_progress' : 'submitted');
      const { connections: connectionsToSave, revenue_last_12_months, ...allOrganisationFields } = organisation;
      // FIG-345 · UC-228: an existing merchant's POC changes only in Merchant
      // Contacts, which keeps the contacts list and organisations.poc_* in step.
      const organisationFields = lockPointOfContact
        ? (({ poc_name: _pocName, poc_email: _pocEmail, poc_phone: _pocPhone, ...rest }) => rest)(allOrganisationFields)
        : allOrganisationFields;
      const saveExistingOrganisation = async (id: string, onboardingState: OnboardingState) => {
        const { error } = await saveOrganisationConnections(id, connectionsToSave, {
          ...organisationFields,
          onboarding_state: onboardingState,
        });
        if (error) return error;
        // organisations-manager-v3 doesn't update revenue, so a correction made
        // after the Step 1 draft insert is written directly (admin-only modal).
        const { error: revenueError } = await supabase
          .from('organisations')
          .update({ revenue_last_12_months })
          .eq('id', id);
        return revenueError;
      };

      if (saveAsDraft) {
        // Handle converting submitted organization to draft
        if (editMode && organizationId && !isDraft) {
          // Update existing organization to draft status
          const updateError = await saveExistingOrganisation(organizationId, onboardingStateFor(true));

          if (updateError) throw updateError;
          toast.success('Organisation converted to draft successfully');
        }
        // Handle updating existing draft
        else if (editMode && organizationId && isDraft) {
          // Update existing draft organization
          const updateError = await saveExistingOrganisation(organizationId, onboardingStateFor(true));

          if (updateError) throw updateError;
          toast.success('Draft updated successfully');
        }
        // Handle updating draft we created earlier (for CSV import)
        else if (draftOrgId) {
          const updateError = await saveExistingOrganisation(draftOrgId, onboardingStateFor(true));

          if (updateError) throw updateError;
          toast.success('Draft saved successfully');
        }
        // Handle creating new draft (fallback)
        else {
          const { error: insertError } = await supabase
            .from('organisations')
            .insert(withOnboardingColumns<TablesInsert<'organisations'>>(organisation, {
              onboarding_state: onboardingStateFor(true),
              ...(isTestClient ? { status: 'test' as const } : {}),
            }));

          if (insertError) throw insertError;
          toast.success('Draft saved successfully');
        }
      } else {
        // Step 2 data upload is optional when an admin adds a merchant (REQ-034).
        if (connectors.length === 0 && !editMode && !isAdminCreate) {
          toast.error('Please import at least one CSV file before submitting');
          setSubmitting(false);
          return;
        }

        let error;
        if (editMode && organizationId && !isDraft) {
          // Update existing submitted organization
          error = await saveExistingOrganisation(organizationId, onboardingStateFor(false));

          if (!error) {
            toast.success('Organisation updated successfully');
          }
        } else if (editMode && organizationId && isDraft) {
          // Convert draft to submitted by updating status
          error = await saveExistingOrganisation(organizationId, onboardingStateFor(false));

          if (!error) {
            toast.success('Draft submitted successfully as organisation');
          }
        } else if (draftOrgId) {
          // Update the draft organization we created earlier
          error = await saveExistingOrganisation(draftOrgId, onboardingStateFor(false));

          if (!error) {
            toast.success(isAdminCreate ? 'Merchant added' : 'Organisation created successfully with CSV data');
          }
        } else {
          // Create new submitted organization (fallback)
          const { error: insertError } = await supabase
            .from('organisations')
            .insert(withOnboardingColumns<TablesInsert<'organisations'>>(organisation, {
              onboarding_state: onboardingStateFor(false),
              ...(isTestClient ? { status: 'test' as const } : {}),
            }));

          error = insertError;

          if (!error) {
            toast.success('Organisation created successfully with CSV data');
          }
        }

        if (error) throw error;

        if (isAdminCreate && draftOrgId) {
          // Fire-and-forget: history must not block adding the merchant.
          supabase.auth.getUser().then(({ data: { user } }) => {
            if (!user?.email) return;
            void logMerchantCreated(user.email, draftOrgId, organisation.name, { isTestClient, userId: user.id });
          });
        }

        // Seed inventory_events from snapshot data for the newly submitted org.
        // Fire-and-forget — backfill can be slow; don't block the submission UX.
        const backfillOrgId = organizationId || draftOrgId;
        if (backfillOrgId) {
          invokeFunction(supabase, 'backfill-org-master-products-v1', { body: { organisationId: backfillOrgId } })
            .then(({ error: bfErr }) => {
              if (bfErr) console.error('[backfill] failed:', bfErr);
            });
        }
      }

      // Clear session storage and state after successful submission
      const sessionKey = getSessionKey();
      sessionStorage.removeItem(sessionKey);
      connectors.forEach(c => sessionStorage.removeItem(`snapshot_conn_id_${c.id}`));

      setCurrentStep(1);
      setOrganizationData(null);
      setCsvConnectors([]);
      setIsTestClient(false);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error(`Error ${saveAsDraft ? 'saving draft' : (editMode ? 'updating' : 'creating')} organisation:`, error);

      if (error.code === '23505' && error.message.includes('organisations_ein_unique')) {
        toast.error('An organisation with this EIN already exists');
      } else {
        toast.error(`Failed to ${saveAsDraft ? 'save draft' : (editMode ? 'update' : 'create')} organisation`);
      }
    } finally {
      setSubmitting(false);
      setSavingDraft(false);
    }
  };

  const handleClose = () => {
    // Save CSV connectors to session storage before closing (if any exist)
    if (csvConnectors.length > 0) {
      const sessionKey = getSessionKey();
      sessionStorage.setItem(sessionKey, JSON.stringify(csvConnectors));
    }

    // Only reset step and organization data, keep CSV connectors for restoration
    setCurrentStep(1);
    setOrganizationData(null);
    onOpenChange(false);
  };

  // Function to clear CSV connectors (for switching organizations or after errors)
  const clearCsvConnectors = () => {
    const sessionKey = getSessionKey();
    sessionStorage.removeItem(sessionKey);
    setCsvConnectors([]);
  };

  // Create title based on mode
  const getTitle = () => {
    if (editMode) {
      if (isDraft) {
        return 'Edit Draft Organisation';
      }
      return 'Edit Organisation';
    }
    return 'Add Merchant';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-brand-primary mb-4" />
            <p className="text-lg">Loading organization data...</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{getTitle()} {isAdminCreate ? '–' : '-'} Step {currentStep} of 2</DialogTitle>
              <DialogDescription>
                {isAdminCreate
                  ? (currentStep === 1 ? "Enter the merchant's details below." : 'Upload data for this merchant.')
                  : (currentStep === 1 ? 'Enter organisation details below.' : 'Upload CSV data for this organisation.')}
              </DialogDescription>
            </DialogHeader>

            {/* Progress indicator */}
            <div className="w-full flex mb-4 mt-2">
              <div className="flex-1 mr-1">
                <div
                  className={`h-2 rounded-l ${currentStep === 1 ? 'bg-brand-primary' : 'bg-gray-200'}`}
                ></div>
              </div>
              <div className="flex-1 ml-1">
                <div
                  className={`h-2 rounded-r ${currentStep === 2 ? 'bg-brand-primary' : 'bg-gray-200'}`}
                ></div>
              </div>
            </div>

            {currentStep === 1 && (
              <OrganisationDetailsForm
                onSubmit={handleStepOneComplete}
                onCancel={() => onOpenChange(false)}
                isSubmitting={submitting}
                initialData={organizationData}
                organizationId={organizationId}
                isAdminEdit={isAdminEdit}
                mode={isAdminCreate ? 'adminCreate' : 'default'}
                initialIsTestClient={isTestClient}
                lockPointOfContact={lockPointOfContact}
              />
            )}

            {currentStep === 2 && (
              <CsvImportStep
                onBack={handleBack}
                onSubmit={handleSubmit}
                onSaveAsDraft={handleSaveAsDraft}
                isSubmitting={submitting}
                isSavingDraft={savingDraft}
                csvConnectors={csvConnectors}
                setCsvConnectors={(connectors) => {
                  setCsvConnectors(connectors);
                  // Save to session storage whenever connectors change
                  if (connectors.length > 0) {
                    const sessionKey = getSessionKey();
                    sessionStorage.setItem(sessionKey, JSON.stringify(connectors));
                  }
                }}
                showSaveDraftButton={!isAdminCreate}
                allowSubmitWithoutFiles={isAdminCreate}
                submitLabel={isAdminCreate ? 'Add Merchant' : undefined}
                intro={isAdminCreate
                  ? 'Optional. Import data from e-commerce platforms (Amazon, Shopify, etc.), banking, or accounting systems.'
                  : undefined}
                organizationId={draftOrgId || organizationId}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MultiStepOrganisationModal;
